import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import type { ApiErrorBody } from '@lkq/shared';

// THE axios instance (commandment 14: exactly one). Cookies ride
// automatically (first-party via the dev proxy / same-site in production).
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  withCredentials: true,
  timeout: 20_000,
});

export type ApiError = AxiosError<ApiErrorBody>;

export const errorCode = (error: unknown): string =>
  (error as ApiError)?.response?.data?.error?.code ?? 'unknown';

// User-facing copy comes from the `errors` locale namespace via the useErrorMessage
// hook (commandment 6) — the raw server message is never rendered directly.

// Single-flight refresh: one refresh attempt shared by all 401s, retry once.
let refreshing: Promise<void> | null = null;
const listeners: Array<() => void> = [];
const blockedListeners: Array<(status: string) => void> = [];

/** AuthContext registers a handler so a dead session routes to /login. */
export function onSessionExpired(handler: () => void): void {
  listeners.push(handler);
}

/** AuthContext registers a handler so a mid-session membership block (the API
 *  returning 403 garage_suspended/expired/archived/rejected) routes the member
 *  to the branded block screen (docs/06 — "next request fails the status gate"). */
export function onMembershipBlocked(handler: (status: string) => void): void {
  blockedListeners.push(handler);
}

api.interceptors.response.use(undefined, async (error: ApiError) => {
  const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
  const status = error.response?.status;
  const url = config?.url ?? '';

  if (status === 401 && config && !config._retried && !url.includes('/auth/login') && !url.includes('/auth/refresh')) {
    config._retried = true;
    try {
      refreshing ??= api.post('/auth/refresh').then(() => undefined).finally(() => { refreshing = null; });
      await refreshing;
      return api.request(config);
    } catch {
      listeners.forEach((fn) => fn());
    }
  }

  // Membership blocked mid-session → route to the branded block screen.
  if (status === 403) {
    const code = error.response?.data?.error?.code ?? '';
    if (code.startsWith('garage_')) blockedListeners.forEach((fn) => fn(code.replace('garage_', '')));
  }
  throw error;
});
